/**
 * @file   src\Pages\CWOPaymentInformation.tsx
 * @brief  BankInformation page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, Container, Button, Row, Col, useOutletContext, useNavigate, useEffect, useState, useRef } from '../utils/thirdpartyComponents';
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWRadio from '../components/CWRadio';
import { AppContextType, onwercrwashbankFormData, ownerlocationbankFormData } from '../interfaces/generalInterface';
import { carbankingInfoSchema, locbankingInfoSchema } from '../validations/cwobankingInfoSchema';
import * as Yup from 'yup'
import { pageURLs } from '../utils/pageUrls';
import { MessageToaster } from "../utils/toastUtil";
import { PaymentType } from '../utils/enums';
import axios from '../config/axiosConfig';
import FullLoader from '../components/FullLoader';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';
import { CompanyApi, LocationApi } from "../utils/apiUrls";
import CWCheckbox from '../components/CWCheckbox';
import { Accordion } from 'react-bootstrap';
import applePayInstruction1 from "../assets/images/apple-pay-instruction1.png";
import applePayInstruction2 from "../assets/images/apple-pay-instruction2.png";
import { hasInvalidStripeSecretKey, STRIPE_SECRET_KEY_LIVE_ERROR, stripePublicKeyCwInputLabel, stripeSecretKeyCwInputLabel } from '../components/stripePaymentInputLabels';


const CWOPaymentInformation = () => {

    const [loading, setLoading] = useState(false);
    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    const navigate = useNavigate();

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    useEffect(() => {
        handleUpdate()
    }, [])
    
    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Payment Method", url: "", showBackButton: false, showMainMenu: true, activeLink: "" });
    };

    const [options, setOptions] = useState<Option[]>([]);

    // Convert PaymentType enum to an array of options
    const paymentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(PaymentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: PaymentType[key as keyof typeof PaymentType],
            value: Number(key)
        }))
    ];

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    // Initialize states for owner formData 
    const [formData, setFormData] = useState({
        information:true,
        locationId:0,
        PaymentMethod: 1,
        stripeSecretKey: '',
        stripePublicKey: '',
    });

    const [ownercarwashformData, setOwnercarwashFormData] = useState<onwercrwashbankFormData>({
        PaymentMethod: formData.PaymentMethod,
        paymentKeys: {
            stripeSecretKey: '',
            stripePublicKey: '',
        },
    });

    // State to handle errors
    const [errors, setErrors] = useState<any>({});

    //State to handle location form data
    const [ownerlocationformData, setOwnerlocationFormData] = useState<ownerlocationbankFormData>({
        locationId:0,
        PaymentMethod: 0,
        paymentKeys: {
            stripeSecretKey: '',
            stripePublicKey: '',
        },
        information : false
    });

    // State to handle location errors
    const [locationerrors, setLocationErrors] = useState<any>({});

    const onChangeAction = (value: boolean) => {

        if(value === true)
        {
            formData.stripePublicKey=companybank?.stripePublicKey ? companybank?.stripePublicKey.trim() : "" ;
            formData.stripeSecretKey=companybank?.stripeSecretKey ? companybank?.stripeSecretKey.trim() : "";
            formData.PaymentMethod = companybank?.PaymentMethod ? companybank?.PaymentMethod : 0;
        }
        else
        {
            formData.stripePublicKey=locbankformData?.stripePublicKey ? locbankformData?.stripePublicKey.trim() : "" ;
            formData.stripeSecretKey=locbankformData?.stripeSecretKey ? locbankformData?.stripeSecretKey.trim() : "";
            formData.locationId = locbankformData?.locationId ? locbankformData?.locationId : 0;
            formData.PaymentMethod = locbankformData?.PaymentMethod ? locbankformData?.PaymentMethod : 0;
        }

        const newFormData = {
            ...formData,
            'information': value,
        };

        setFormData(newFormData);
    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };

        setFormData(newFormData);
    };


    // Triggers whenever options is selected
    const lochandleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(locationerrors);
        if (keys.includes(name)) {
            setLocationErrors({
                ...locationerrors,
                [name]: ""
            })
        }

        setOwnerlocationFormData({
            ...ownerlocationformData,
            [name]: value,
        });
    };

    // On input change
    const lochandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(locationerrors);
        if (keys.includes(name)) {
            setLocationErrors({
                ...locationerrors,
                [name]: ""
            })
        }

        const newFormData = {
            ...ownerlocationformData,
            [name]: value,
        };

        setOwnerlocationFormData(newFormData);
    };

    useEffect(() => {
        if(formData.information === true)
        {
            setOwnercarwashFormData({
                PaymentMethod : formData.PaymentMethod,
                    paymentKeys:{
                        stripeSecretKey: formData.stripeSecretKey.trim(),
                        stripePublicKey: formData.stripePublicKey.trim()
                    }
                });
        } else {
            setOwnercarwashFormData({
                PaymentMethod : locbankformData.PaymentMethod,
                    paymentKeys:{
                        stripeSecretKey: locbankformData.stripeSecretKey.trim(),
                        stripePublicKey: locbankformData.stripePublicKey.trim()
                    }
                });
        }
        
    }, [formData]);

    const validateForm = async (): Promise<boolean> => {
        if (formData.PaymentMethod === 1 && hasInvalidStripeSecretKey(formData.stripeSecretKey)) {
            toaster.toastError(STRIPE_SECRET_KEY_LIVE_ERROR);
            return false;
        }
        try {
            setLoading(true); // Show loader
            await carbankingInfoSchema.validate(formData, { abortEarly: false });
                const paymentData = {
                    "PaymentMethod" : ownercarwashformData.PaymentMethod,
                    "paymentKeys" : ownercarwashformData.paymentKeys
                }
                const { data } = await axios.post(CompanyApi.CARWASH_OWNER_VALIDATE_COMPANY_PAYMENT_API_KEYS, paymentData ) 
                if (data?.error !== 0) {
                    toaster.toastError(data?.message);
                    return false;
                }
                const responseLocationBankUpdate = await axios.post(CompanyApi.CARWASH_OWNER_UPDATE_PAYMENT_METHOD, ownercarwashformData);
                setLoading(false)
                if (responseLocationBankUpdate?.data?.error === 0) {
                    toaster.toastSuccess(responseLocationBankUpdate?.data?.message);
                    navigate(`/${pageURLs.cwo_paymentinformation}`);
                } else {
                    toaster.toastError(responseLocationBankUpdate?.data?.message);
                }

                return true;
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
            return false
        } finally {
            setLoading(false); // Hide loader
        }

    };
         //To validate form on clicking next
    const locationvalidateForm = async (): Promise<boolean> => {
        if (locbankformData.PaymentMethod === 1 && hasInvalidStripeSecretKey(locbankformData.stripeSecretKey)) {
            toaster.toastError(STRIPE_SECRET_KEY_LIVE_ERROR);
            return false;
        }
        try {
            setLoading(true); // Show loader
            await locbankingInfoSchema.validate(locbankformData, { abortEarly: false });
            const locationPaymentData = {
                locationId:ownerlocationformData.locationId,
                PaymentMethod: locbankformData.PaymentMethod,
                paymentKeys: {
                    stripeSecretKey: locbankformData.stripeSecretKey.trim(),
                    stripePublicKey: locbankformData.stripePublicKey.trim(),
                },
                HasDefaultAccount :locbankformData.HasDefaultAccount 
            }
            const responseLocationBankUpdate = await axios.post(LocationApi.CARWASH_OWNER_UPDATE_LOCATION_PAYMENT_METHOD, locationPaymentData);
            
            if (responseLocationBankUpdate?.data?.error === 0) {
                toaster.toastSuccess(responseLocationBankUpdate?.data?.message);
                navigate(`/${pageURLs.cwo_paymentinformation}`);
            } else {
                toaster.toastError(responseLocationBankUpdate?.data?.message);
            }

            setLocationErrors({});
            return true;
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setLocationErrors(newErrors);
            }
            return false
        } finally {
            setLoading(false); // Hide loader
        }

    };

    // State to company bank details
    const [companybank, setCompanybank] = useState ({
        stripeSecretKey: "",
        stripePublicKey: "",
        PaymentMethod : 1
    }
    );

    //State to handle form data
    const [locbankformData, setLocbankFormData] = useState({
        locationId:0,
        PaymentMethod: 0,
        stripeSecretKey: "",
        stripePublicKey: "",
        HasDefaultAccount : 0
    }
    );

    const getCompanyLocations = async () => {
        try {

            const { data } =await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setOptions(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

        // To get company bank details
        const getCompanybank = async () => {
            try {
                const { data } = await axios.get(CompanyApi.CARWASH_OWNER_GET_COMPANY_BANK_DETAILS)
                if (data?.error === 0) {
                    const paymentDetails = data.data.paymentDetails;
                    if (paymentDetails && paymentDetails !== "") {
                        setCompanybank({
                            PaymentMethod: data?.data?.PaymentMethod,
                            stripeSecretKey: paymentDetails?.stripeSecretKey,
                            stripePublicKey: paymentDetails?.stripePublicKey
                        });
                        setFormData({
                            information:formData.information,
                            locationId: 0,
                            PaymentMethod: data?.data?.PaymentMethod,
                            stripeSecretKey: paymentDetails?.stripeSecretKey,
                            stripePublicKey: paymentDetails?.stripePublicKey
                        });
                    }
                }
            } catch (error: any) {
                throw new Error(error.response?.data?.message || error.message);
            }
        }
    
        // To get company bank details
        const getLocationbank = async () => {
            try {
                setLocationErrors({});
                let param = '';
                param += ownerlocationformData.locationId;
                const { data } = await axios.get(LocationApi.CARWASH_OWNER_GET_LOCATION_BANK_DETAILS+ param);
                if (data?.error === 0) {
                    const paymentDetails = data.data.paymentDetails;
                    if (paymentDetails && paymentDetails !== "") {
                        setLocbankFormData({
                            locationId: data?.data?.locationId,
                            PaymentMethod:  data?.data?.PaymentMethod,
                            stripeSecretKey: paymentDetails?.stripeSecretKey,
                            stripePublicKey: paymentDetails?.stripePublicKey,
                            HasDefaultAccount : data?.data?.HasDefaultAccount,
                        });
                    } else {
                        setLocbankFormData({
                            locationId:  ownerlocationformData.locationId,
                            PaymentMethod:   ownerlocationformData.PaymentMethod,
                            stripeSecretKey: '',
                            stripePublicKey: '',
                            HasDefaultAccount :  data?.data?.HasDefaultAccount
                        });
                    }
                }
            } catch (error: any) {
                throw new Error(error.response?.data?.message || error.message);
            }
        }
    
        useEffect(() => {
                getCompanyLocations();  // company locations
                getCompanybank(); // company bank
              //  getLocationbank(); //location bank
            
        }, []);

        useEffect(() => {
            if(ownerlocationformData.locationId > 0) {
                getLocationbank(); //location bank
            } else {
                setLocbankFormData({
                    locationId: 0,
                    PaymentMethod:  0,
                    stripeSecretKey: '',
                    stripePublicKey: '',
                    HasDefaultAccount : 0
                });
            }
         
        }, [ownerlocationformData.locationId]);
    return (

        <div className='hardware-listing-container addnew-item-wrap'>
            <Container>
                <div className="hardware-list-wrap">
                    <div className="form-inner mx-auto">
                        <div>
                            <div className="radio-field-group d-flex">
                                <CWRadio
                                    id="carwashinformation"
                                    name="information"
                                    label="Car Wash Information"
                                    checked={formData.information}
                                    onChange={() => onChangeAction(true)}
                                />
                                <CWRadio
                                    id="locationinformation"
                                    name="information"
                                    label="Location Information"
                                    checked={!formData.information}
                                    onChange={() => onChangeAction(false)}
                                />
                            </div>
                        </div>

                       </div>
                       <div className="form-inner mx-auto">
                        <Accordion defaultActiveKey="" className='stripe-accordion'>
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Stripe Instructions</Accordion.Header>
                                <Accordion.Body>
                                    <div className="stripe-payment-note mb-5">
                                        <p>To accept real payments, you need to use your Stripe production keys. Make sure you're in the Stripe production dashboard. </p>
                                        <p>To switch to production mode(if you are in Test Mode):</p>
                                        <ul>
                                            <li>1. Go to the left-hand menu.</li>
                                            <li>2. Click on your account name.</li>
                                            <li>3. A toggle popup will appear.</li>
                                            <li>4. Click on "Exit test mode".</li>
                                        </ul>
                                        <p>Once you're in production mode, you can copy your live public key and secret key from the API keys section.</p>
                                        <p>To find your keys for copying and pasting to carwashapp.com</p>
                                        <ul>
                                            <li>1.Log into your Stripe account</li>
                                            <li>2.Click Developers, bottom left of screen</li>
                                            <li>3.Go to API Keys section </li>
                                        </ul>
                                        <p>You’ll see two types of keys:</p>
                                        <ul>
                                            <li>Publishable Key (Public) → Starts with pk_live_....</li>
                                            <li>Secret Key (Private) → Starts with sk_live_....</li>
                                        </ul>

                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>Apple pay Instructions</Accordion.Header>
                                <Accordion.Body>
                                    <div className="stripe-payment-note mb-5">
                                        <p>Turning on Apple Pay:</p>
                                        <p>Apple Pay must be Active in Stripe Dashboard →</p>
                                        <ul>
                                            <li>1. Click Settings (Gear Icon)</li>
                                            <li>2. Click Product settings</li>
                                            <li>3. Click Payments</li>
                                            <li>4. Click Payment Methods</li>
                                            <li>5. Click Default</li>
                                            <li>6. Apple Pay please click Turn On</li>
                                            <li>7. Click the three dots</li>
                                            <li>8. Click Configure domains</li>
                                            <li>9. Click Add new domain</li>
                                            <li>10. Enter and add the domain: app.carwashapp.com</li>
                                        </ul>
                                        <p>Once this set up is done, sign in to the app of your
                                            location and go to subscriptions in bay or tunnel,
                                            when you go to purchase a membership an Apple Pay
                                            button will display, or if at the location it will also
                                            display for a single wash.</p>
 <img
        src={applePayInstruction1}
        alt="Apple Pay Instruction"
        style={{ maxWidth: "100%", height: "auto" }}
      />   
      <p>  </p>
 <img
        src={applePayInstruction2}
        alt="Apple Pay Instruction"
        style={{ maxWidth: "100%", height: "auto" }}
      />  
                                    </div>

                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                    {formData.information && (
                        <div className="cw-form-container">
                        <div className='form-inner mx-auto'>
                    <>
                        
                        <CWSelect
                            id="PaymentMethod"
                            name="PaymentMethod"
                            options={paymentOptions}
                            label="Select Merchant Payment Type"
                            errorMessage={errors.PaymentMethod && errors.PaymentMethod}
                            dropdownGap={0}
                            dropdownPosition="auto"
                            values={paymentOptions.filter(option => option.value === formData.PaymentMethod)}
                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'PaymentMethod')}
                            placeholder="Select Merchant Payment Type"
                            multi={false}
                            searchable={false}
                            isMandatory
                        />
                        {formData.PaymentMethod === 1 && (
                        <>
                            <CWInput
                                id="stripeSecretKey"
                                name="stripeSecretKey"
                                type="text"
                                label={stripeSecretKeyCwInputLabel}
                                value={formData.stripeSecretKey}
                                onChange={handleChange}
                                errorMessage={errors.stripeSecretKey}
                                ref={(el) => (refs.current.stripeSecretKey = el)}
                                autoComplete="off"
                                isMandatory
                                autoFocus
                            />
                            <CWInput
                                id="stripePublicKey"
                                name="stripePublicKey"
                                type="text"
                                label={stripePublicKeyCwInputLabel}
                                value={formData.stripePublicKey}
                                onChange={handleChange}
                                errorMessage={errors.stripePublicKey}
                                isMandatory
                                ref={(el) => (refs.current.stripePublicKey = el)}
                            />
                        </>
                        )}
                        {formData.PaymentMethod === 2 && (
                        <>
                        {/* use when world pay is ready */}
                            <CWInput
                                id="SecretKey"
                                name="SecretKey"
                                type="text"
                                label="SecretKey"
                                onChange={handleChange}
                                // value={formData.SecretKey}
                                // errorMessage={errors.SecretKey}
                                // ref={(el) => (refs.current.SecretKey = el)}
                                autoComplete="off"
                                isMandatory
                                autoFocus
                            />
                            <CWInput
                                id="PublicKey"
                                name="PublicKey"
                                type="text"
                                label="Account Name"
                                onChange={handleChange}
                                // value={formData.PublicKey}
                                // errorMessage={errors.PublicKey}
                                // ref={(el) => (refs.current.PublicKey = el)}
                                isMandatory
                            />
                        </>
                        )}
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => navigate(`/${pageURLs.dashboard}`)}>Cancel</Button>
                            <Button title="Save" variant="primary" className="primary-btn" type="button" onClick={validateForm}>Save</Button>
                        </div>
                        
                    </>
                    </div>
                    </div>
                    )}
                    {!formData.information && (
                    <>
                        <h5 className='title f-bold'>Select Location</h5>
                        <Row>
                            <Col md={12}>
                                <CWSelect
                                    id="locationId"
                                    name="locationId"
                                    options={options}
                                    values={options.filter(option => option.value === locbankformData.locationId)}
                                    onChange={(selectedOptions) => lochandleSelectChange(selectedOptions[0], 'locationId')}
                                    label="Select Car Wash Location"
                                    errorMessage={locationerrors.locationId && locationerrors.locationId}
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select"
                                    multi={false}
                                    isMandatory
                                    className="no-input"
                                />
                            </Col>
                        </Row> 
                        <CWSelect
                            id="PaymentMethod"
                            name="PaymentMethod"
                            options={paymentOptions}
                            label="Select Merchant Payment Type"
                            errorMessage={locationerrors.PaymentMethod && locationerrors.PaymentMethod}
                            dropdownGap={0}
                            dropdownPosition="auto"
                            values={paymentOptions.filter(option => option.value === locbankformData.PaymentMethod)}
                            onChange={(selectedOptions) => lochandleSelectChange(selectedOptions[0], 'PaymentMethod')}
                            placeholder="Select Merchant Payment Type"
                            multi={false}
                            searchable={false}
                            isMandatory
                            disabled= {locbankformData.HasDefaultAccount === 1 ?  true : false}
                        />
                        {locbankformData.PaymentMethod === 1 && (
                        <>
                            <CWInput
                                id="stripeSecretKey"
                                name="stripeSecretKey"
                                type="text"
                                label={stripeSecretKeyCwInputLabel}
                                value={locbankformData.stripeSecretKey}
                                onChange={lochandleChange}
                                errorMessage={locationerrors.stripeSecretKey}
                                ref={(el) => (refs.current.stripeSecretKey = el)}
                                autoComplete="off"
                                isMandatory
                                autoFocus
                                readOnly= {locbankformData.HasDefaultAccount === 1 ?  true : false}
                            />
                            <CWInput
                                id="stripePublicKey"
                                name="stripePublicKey"
                                type="text"
                                label={stripePublicKeyCwInputLabel}
                                value={locbankformData.stripePublicKey}
                                onChange={lochandleChange}
                                errorMessage={locationerrors.stripePublicKey}
                                isMandatory
                                ref={(el) => (refs.current.stripePublicKey = el)}
                                readOnly= {locbankformData.HasDefaultAccount === 1 ?  true : false}
                            />
                            <CWCheckbox
                                id="billingaddress"
                                name="billingaddress"
                                label="Company Account"
                                checked={locbankformData.HasDefaultAccount === 1 ?  true : false}
                                disabled={true}
                            />
                        </>
                        )}
                        {locbankformData.PaymentMethod === 2 && (
                        <>
                        {/* use when world pay is ready */}
                            <CWInput
                                id="SecretKey"
                                name="SecretKey"
                                type="text"
                                label="SecretKey"
                                onChange={lochandleChange}
                                // value={formData.SecretKey}
                                // errorMessage={locationerrors.SecretKey}
                                // ref={(el) => (refs.current.SecretKey = el)}
                                autoComplete="off"
                                isMandatory
                                autoFocus
                            />
                            <CWInput
                                id="PublicKey"
                                name="PublicKey"
                                type="text"
                                label="Account Name"
                                onChange={lochandleChange}
                                // value={formData.PublicKey}
                                // errorMessage={locationerrors.PublicKey}
                                // ref={(el) => (refs.current.PublicKey = el)}
                                isMandatory
                            />
                        </>
                        )}
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => navigate(`/${pageURLs.dashboard}`)}>Cancel</Button>
                            <Button title="Save" variant="primary" className="primary-btn" type="button" onClick={locationvalidateForm}>Save</Button>
                        </div>
                    </>
                    )}
                    
                    </div>
                </div>
            </Container >
            {loading && <FullLoader />}
        </div >
    )
}

export default CWOPaymentInformation;
